import { CheckIcon, HeartIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useT } from "@/i18n";
import { readableOn } from "@/lib/team-color";
import { useFans } from "@/lib/useFans";
import { cn } from "@/lib/utils";
import type { GameState } from "../../../shared/types";

export function LiveSupport({ state, connected }: { state: GameState; connected: boolean }) {
  if (
    !state.scheduleGameId ||
    !state.teamA.teamId ||
    !state.teamB.teamId ||
    !["h1", "half", "h2"].includes(state.period)
  )
    return null;
  return (
    <SupportPoll
      key={`${state.scheduleGameId}:${state.teamA.teamId}:${state.teamB.teamId}`}
      state={state}
      connected={connected}
    />
  );
}

function SupportPoll({ state, connected }: { state: GameState; connected: boolean }) {
  const t = useT();
  const fans = useFans();
  const [cheer, setCheer] = useState<string | null>(null);
  const poll = fans.data?.support?.find(
    (p) =>
      p.gameId === state.scheduleGameId &&
      p.teams[0]?.id === state.teamA.teamId &&
      p.teams[1]?.id === state.teamB.teamId,
  );
  if (!poll?.open && !fans.error) return null;
  const teams = [state.teamA, state.teamB];
  const counts = new Map(poll?.counts ?? []);
  const shareA = poll?.total
    ? Math.round((100 * (counts.get(state.teamA.teamId!) ?? 0)) / poll.total)
    : 0;
  return (
    <Card className="gap-2.5 p-4" aria-label={t("fan.support.title")}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-sm font-semibold">{t("fan.support.title")}</h3>
        <span className="text-xs text-muted-foreground">
          {poll?.total ? t("fan.support.count", { count: poll.total }) : t("fan.support.first")}
        </span>
      </div>
      {poll?.open && (
        <>
          <div className="grid grid-cols-2 gap-3">
            {teams.map((team, i) => {
              const selected = poll.mine === team.teamId;
              return (
                <div key={team.teamId} className="relative min-w-0">
                  <Button
                    variant={selected ? "default" : "outline"}
                    className="w-full justify-between gap-2"
                    aria-label={t("fan.support.team", { team: team.name })}
                    aria-pressed={selected}
                    disabled={fans.busy || !connected}
                    onClick={async () => {
                      if (selected) return;
                      setCheer(null);
                      if (
                        await fans.vote({
                          kind: "support",
                          gameId: poll.gameId,
                          teamId: team.teamId!,
                        })
                      )
                        setCheer(team.teamId);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      {selected ? (
                        <CheckIcon aria-hidden="true" />
                      ) : (
                        <HeartIcon aria-hidden="true" />
                      )}
                      <span className="team-code text-sm">{team.short}</span>
                    </span>
                    <span className="tabular text-xs">
                      {poll.total ? `${i === 0 ? shareA : 100 - shareA}%` : "—"}
                    </span>
                  </Button>
                  {cheer === team.teamId && (
                    <HeartIcon
                      aria-hidden="true"
                      className="pointer-events-none absolute -top-2 right-3 size-4 fill-brand text-brand-ink motion-reduce:hidden motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:slide-in-from-bottom-2 motion-safe:duration-500 dark:text-brand"
                      onAnimationEnd={() => setCheer(null)}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
            {teams.map((team) => (
              <span
                key={team.teamId}
                className="motion-safe:transition-[width] motion-safe:duration-500"
                style={{
                  width: `${poll.total ? (100 * (counts.get(team.teamId!) ?? 0)) / poll.total : 0}%`,
                  background: readableOn(team.color).bg,
                }}
              />
            ))}
          </div>
        </>
      )}
      <div aria-live="polite" className={cn("text-xs", !fans.error && "sr-only")}>
        {fans.error ? (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-destructive">
            {fans.error}
            <Button variant="outline" size="sm" onClick={fans.refresh} disabled={fans.busy}>
              {t("fan.retry")}
            </Button>
          </div>
        ) : fans.saved && poll?.mine ? (
          t("fan.support.saved", {
            team: teams.find((team) => team.teamId === poll.mine)?.name ?? "",
          })
        ) : null}
      </div>
    </Card>
  );
}
